import React, { useEffect, useMemo, useState } from "react";
import {
  IonBadge,
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonDatetime,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonRadio,
  IonRadioGroup,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTextarea,
  IonToast,
  IonPage,
  IonText,
} from "@ionic/react";
import Header from "src/components/Header";
import {
  buscarSupervisoresVisitas,
  crearEvaluacionVisitas,
  listarEvaluacionesVisitas,
  listarMotivosVisitas,
  listarOpcionesVisitas,
  listarPreguntasVisitas,
  obtenerContextoVisitas,
  EvaluacionVisitasItem,
  MotivoVisita,
  OpcionVisita,
  PreguntaVisita,
  ContextoVisita,
  SupervisorVisita,
} from "src/services/evaluacionVisitas";
import { fetchPuntoServicio, IfetchPuntoServicio } from "src/services/PedidoDeProducto/pedido.producto.services";

type RespuestasMap = Record<number, string>;

const normalizarFecha = (value: string | string[] | null | undefined) => {
  if (!value) return undefined;
  const raw = Array.isArray(value) ? value[0] : value;
  if (!raw) return undefined;
  return raw.toString().slice(0, 10);
};

const normalizarTexto = (valor: string | null | undefined) =>
  String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const nombreSupervisor = (supervisor: Pick<SupervisorVisita, "nombre" | "apellido" | "nombre_completo">) => {
  const completo = String(supervisor.nombre_completo || "").trim();
  if (completo) return completo;
  return [supervisor.nombre, supervisor.apellido].filter(Boolean).join(" ").trim();
};

const esPreguntaConSupervisor = (pregunta: PreguntaVisita) => {
  const texto = normalizarTexto(pregunta.pregunta);
  return texto.includes("punto de servicio") && (texto.includes("supervisor") || texto.includes("supervidor"));
};

const EvaluacionVisitas: React.FC = () => {
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; msg: string; color?: string }>({ show: false, msg: "" });

  const [motivos, setMotivos] = useState<MotivoVisita[]>([]);
  const [preguntas, setPreguntas] = useState<PreguntaVisita[]>([]);
  const [opciones, setOpciones] = useState<Record<string, OpcionVisita[]>>({});

  const [psBusqueda, setPsBusqueda] = useState("");
  const [psResultados, setPsResultados] = useState<IfetchPuntoServicio[]>([]);
  const [psBuscando, setPsBuscando] = useState(false);
  const [psErrorBusqueda, setPsErrorBusqueda] = useState<string | null>(null);
  const [psSeleccionado, setPsSeleccionado] = useState<IfetchPuntoServicio | null>(null);

  const [contexto, setContexto] = useState<ContextoVisita | null>(null);

  const [fechaVisita, setFechaVisita] = useState<string | undefined>(undefined);
  const [motivoId, setMotivoId] = useState<number | undefined>(undefined);
  const [comentarios, setComentarios] = useState("");
  const [planAccion, setPlanAccion] = useState("");
  const [fechaPlan, setFechaPlan] = useState<string | undefined>(undefined);
  const [respuestas, setRespuestas] = useState<RespuestasMap>({});
  const [supervisorLegajo, setSupervisorLegajo] = useState<number | undefined>(undefined);
  const [supervisorSeleccionado, setSupervisorSeleccionado] = useState<SupervisorVisita | null>(null);
  const [supervisoresPs, setSupervisoresPs] = useState<SupervisorVisita[]>([]);
  const [supervisoresPsBuscando, setSupervisoresPsBuscando] = useState(false);
  const [supervisoresPsError, setSupervisoresPsError] = useState<string | null>(null);
  const [supervisorBusqueda, setSupervisorBusqueda] = useState("");
  const [supervisorResultados, setSupervisorResultados] = useState<SupervisorVisita[]>([]);
  const [supervisorBuscando, setSupervisorBuscando] = useState(false);
  const [supervisorErrorBusqueda, setSupervisorErrorBusqueda] = useState<string | null>(null);
  const [tieneSupervisor, setTieneSupervisor] = useState<"SI" | "NO">("SI");

  const [evaluaciones, setEvaluaciones] = useState<EvaluacionVisitasItem[]>([]);
  const [listando, setListando] = useState(false);

  const preguntasOrdenadas = useMemo(
    () => [...preguntas].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0)),
    [preguntas]
  );
  const preguntasSupervisor = useMemo(
    () => preguntasOrdenadas.filter((p) => p.id >= 3 && p.id <= 6),
    [preguntasOrdenadas]
  );
  const preguntasPersonal = useMemo(
    () => preguntasOrdenadas.filter((p) => p.id >= 7 && p.id <= 9),
    [preguntasOrdenadas]
  );
  const preguntasIniciales = useMemo(
    () => preguntasOrdenadas.filter((p) => p.id < 3),
    [preguntasOrdenadas]
  );
  const preguntasFinales = useMemo(
    () => preguntasOrdenadas.filter((p) => p.id > 9),
    [preguntasOrdenadas]
  );
  const preguntaTieneSupervisor = useMemo(
    () => preguntasOrdenadas.find(esPreguntaConSupervisor) || null,
    [preguntasOrdenadas]
  );

  const limpiarSeleccionSupervisor = () => {
    setSupervisorLegajo(undefined);
    setSupervisorSeleccionado(null);
  };

  const etiquetaRespuestaPregunta = (pregunta: PreguntaVisita | null, codigo?: string) => {
    if (!pregunta || !codigo) return "";
    const tipo = (pregunta.tipo_respuesta || "").toUpperCase();
    const opcion = (opciones[tipo] || []).find(
      (item) => String(item.codigo || "").trim().toUpperCase() === String(codigo || "").trim().toUpperCase()
    );
    return String(opcion?.etiqueta || codigo || "").trim();
  };

  const estadoSupervisor = useMemo<"SI" | "NO" | "NO_APLICA" | null>(() => {
    if (!preguntaTieneSupervisor) return null;
    const codigo = respuestas[preguntaTieneSupervisor.id];
    if (!codigo) return null;

    const codigoNorm = normalizarTexto(codigo);
    const etiquetaNorm = normalizarTexto(etiquetaRespuestaPregunta(preguntaTieneSupervisor, codigo));

    if (codigoNorm === "si" || etiquetaNorm === "si") return "SI";
    if (codigoNorm === "no" || etiquetaNorm === "no") return "NO";
    if (
      codigoNorm.includes("aplica") ||
      etiquetaNorm.includes("aplica") ||
      codigoNorm === "na" ||
      etiquetaNorm === "na"
    ) {
      return "NO_APLICA";
    }
    return null;
  }, [preguntaTieneSupervisor, respuestas, opciones]);

  const debeMostrarSelectSupervisor = estadoSupervisor === "SI";

  useEffect(() => {
    let activo = true;
    const cargar = async () => {
      setCargando(true);
      try {
        const [mot, preg, opc] = await Promise.all([
          listarMotivosVisitas(),
          listarPreguntasVisitas(),
          listarOpcionesVisitas(),
        ]);
        if (!activo) return;
        setMotivos(mot);
        setPreguntas(preg);
        const mapOpciones: Record<string, OpcionVisita[]> = {};
        opc.forEach((o) => {
          const tipo = (o.tipo_respuesta || "").toUpperCase();
          if (!tipo) return;
          if (!mapOpciones[tipo]) mapOpciones[tipo] = [];
          mapOpciones[tipo].push(o);
        });
        Object.keys(mapOpciones).forEach((k) => {
          mapOpciones[k].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
        });
        setOpciones(mapOpciones);
      } catch (error: any) {
        setToast({ show: true, msg: error?.message || "No se pudo cargar la configuracion.", color: "danger" });
      } finally {
        if (activo) setCargando(false);
      }
    };
    cargar();
    return () => {
      activo = false;
    };
  }, []);

  useEffect(() => {
    let activo = true;
    if (!psSeleccionado?.id) {
      setSupervisoresPs([]);
      setSupervisoresPsError(null);
      setSupervisoresPsBuscando(false);
      return;
    }
    setSupervisoresPsBuscando(true);
    (async () => {
      try {
        const lista = await buscarSupervisoresVisitas(psSeleccionado.id);
        if (!activo) return;
        const unicos = new Map<number, SupervisorVisita>();
        (lista || []).forEach((item) => {
          const legajo = Number(item.legajo);
          if (!legajo) return;
          const nombre = nombreSupervisor(item);
          if (!nombre) return;
          unicos.set(legajo, {
            legajo,
            nombre: String(item.nombre || "").trim(),
            apellido: String(item.apellido || "").trim(),
            nombre_completo: nombre,
            cargo: String(item.cargo || "SUPERVISOR").trim() || "SUPERVISOR",
          });
        });
        const resultados = Array.from(unicos.values()).sort((a, b) =>
          nombreSupervisor(a).localeCompare(nombreSupervisor(b))
        );
        setSupervisoresPs(resultados);
        setSupervisoresPsError(null);
        if (supervisorLegajo && !resultados.some((item) => item.legajo === supervisorLegajo)) {
          limpiarSeleccionSupervisor();
        }
      } catch {
        if (activo) {
          setSupervisoresPs([]);
          setSupervisoresPsError("No se pudieron cargar los supervisores del Punto de Servicio.");
        }
      } finally {
        if (activo) setSupervisoresPsBuscando(false);
      }
    })();
    return () => {
      activo = false;
    };
  }, [psSeleccionado?.id]);

  useEffect(() => {
    if (!debeMostrarSelectSupervisor) {
      limpiarSeleccionSupervisor();
    }
  }, [debeMostrarSelectSupervisor]);


  useEffect(() => {
    let cancelado = false;
    const termino = psBusqueda.trim();
    if (termino.length < 3) {
      setPsResultados([]);
      setPsErrorBusqueda(null);
      setPsBuscando(false);
      return;
    }
    setPsBuscando(true);
    (async () => {
      try {
        const res = await fetchPuntoServicio(termino);
        if (cancelado) return;
        if (res.error) {
          setPsResultados([]);
          setPsErrorBusqueda(res.error);
        } else {
          setPsResultados(res.datos || []);
          setPsErrorBusqueda(null);
        }
      } catch {
        if (!cancelado) setPsErrorBusqueda("No se pudo buscar el Punto de Servicio");
      } finally {
        if (!cancelado) setPsBuscando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, [psBusqueda]);

  useEffect(() => {
    let activo = true;
    const cargarContexto = async () => {
      if (!psSeleccionado?.id) {
        setContexto(null);
        return;
      }
      try {
        const ctx = await obtenerContextoVisitas(
          psSeleccionado.id,
          psSeleccionado.ps_codigo ?? psSeleccionado.objeto
        );
        if (activo) setContexto(ctx);
      } catch (error: any) {
        if (activo) {
          setContexto(null);
          setToast({ show: true, msg: error?.message || "No se pudo obtener el contexto.", color: "danger" });
        }
      }
    };
    cargarContexto();
    return () => {
      activo = false;
    };
  }, [psSeleccionado]);

  const cargarEvaluaciones = async (psId?: number) => {
    setListando(true);
    try {
      const data = await listarEvaluacionesVisitas({ ps_id: psId, limit: 20, offset: 0 });
      setEvaluaciones(data);
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudieron listar las evaluaciones.", color: "warning" });
      setEvaluaciones([]);
    } finally {
      setListando(false);
    }
  };

  useEffect(() => {
    cargarEvaluaciones(psSeleccionado?.id);
  }, [psSeleccionado?.id]);

  const seleccionarPuntoServicio = (ps: IfetchPuntoServicio) => {
    setPsSeleccionado(ps);
    setPsBusqueda("");
    setPsResultados([]);
    setPsErrorBusqueda(null);
    limpiarSeleccionSupervisor();
    setSupervisoresPs([]);
    setSupervisoresPsError(null);
  };

  const limpiarSeleccionPs = () => {
    setPsSeleccionado(null);
    setContexto(null);
    setPsBusqueda("");
    setPsResultados([]);
    setPsErrorBusqueda(null);
    limpiarSeleccionSupervisor();
    setSupervisoresPs([]);
    setSupervisoresPsError(null);
  };

  const seleccionarSupervisor = (sup: SupervisorVisita) => {
    const nombre = nombreSupervisor(sup);
    setSupervisorLegajo(sup.legajo);
    setSupervisorSeleccionado(sup);
    setSupervisorBusqueda(`${nombre} - ${sup.legajo}`);
    setSupervisorResultados([]);
    setSupervisorErrorBusqueda(null);
  };

  const actualizarRespuesta = (preguntaId: number, codigo: string) => {
    setRespuestas((prev) => ({ ...prev, [preguntaId]: codigo }));
  };

  const renderSelectorSupervisor = () => {
    if (!debeMostrarSelectSupervisor) return null;

    return (
      <>
        <IonItem className="supervisor-select-item">
          <IonLabel position="stacked">Supervisor asignado</IonLabel>
          <IonSelect
            interface="alert"
            interfaceOptions={{ cssClass: "supervisor-select-alert" }}
            className="supervisor-select"
            placeholder={
              supervisoresPsBuscando
                ? "Cargando supervisores..."
                : supervisoresPs.length > 0
                  ? "Selecciona un supervisor"
                  : "Sin supervisores asignados"
            }
            value={supervisorLegajo}
            selectedText={
              supervisorSeleccionado ? `${nombreSupervisor(supervisorSeleccionado)} - ${supervisorSeleccionado.legajo}` : undefined
            }
            onIonChange={(e) => {
              const legajo = Number(e.detail.value);
              const supervisor = supervisoresPs.find((item) => item.legajo === legajo) || null;
              setSupervisorLegajo(supervisor?.legajo);
              setSupervisorSeleccionado(supervisor);
            }}
            disabled={!psSeleccionado?.id || supervisoresPsBuscando || supervisoresPs.length === 0}
          >
            {supervisoresPs.map((supervisor) => (
              <IonSelectOption key={supervisor.legajo} value={supervisor.legajo}>
                {`${nombreSupervisor(supervisor)} - ${supervisor.legajo}`}
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>

        <IonItem lines="none">
          <IonLabel className="ion-text-wrap">
            {!psSeleccionado?.id ? (
              <IonText color="medium">Selecciona un Punto de Servicio para cargar supervisores.</IonText>
            ) : supervisoresPsError ? (
              <IonText color="danger">{supervisoresPsError}</IonText>
            ) : supervisoresPsBuscando ? (
              <IonText color="medium">Cargando supervisores asignados al Punto de Servicio.</IonText>
            ) : supervisoresPs.length === 0 ? (
              <IonText color="warning">No hay supervisores asignados al Punto de Servicio.</IonText>
            ) : supervisorSeleccionado ? (
              <IonText color="medium">Supervisor seleccionado.</IonText>
            ) : (
              <IonText color="medium">Selecciona el supervisor asignado al Punto de Servicio.</IonText>
            )}
          </IonLabel>
          {supervisoresPsBuscando && <IonSpinner slot="end" />}
        </IonItem>

        {supervisorSeleccionado && (
          <IonItem lines="none">
            <IonLabel className="ion-text-wrap">
              <strong>Supervisor seleccionado:</strong>{" "}
              {nombreSupervisor(supervisorSeleccionado)} - {supervisorSeleccionado.legajo}
            </IonLabel>
          </IonItem>
        )}
      </>
    );
  };

  const renderPregunta = (p: PreguntaVisita) => {
    const tipo = (p.tipo_respuesta || "").toUpperCase();
    const opcionesTipo = opciones[tipo] || [];
    const nro = p.orden ?? p.id;

    return (
      <IonCard key={p.id}>
        <IonCardContent>
          <IonText>
            {nro}. {p.pregunta}
          </IonText>
          <IonRadioGroup
            value={respuestas[p.id] || ""}
            onIonChange={(e) => actualizarRespuesta(p.id, String(e.detail.value || ""))}
          >
            <IonList>
              {opcionesTipo.map((o) => (
                <IonItem key={o.codigo} lines="none">
                  <IonRadio slot="start" value={o.codigo} />
                  <IonLabel>{o.etiqueta}</IonLabel>
                </IonItem>
              ))}
            </IonList>
          </IonRadioGroup>
          {preguntaTieneSupervisor?.id === p.id && renderSelectorSupervisor()}
        </IonCardContent>
      </IonCard>
    );
  };

  const onGuardar = async () => {
    if (!psSeleccionado?.id) {
      setToast({ show: true, msg: "Selecciona un Punto de Servicio.", color: "warning" });
      return;
    }
    if (!fechaVisita) {
      setToast({ show: true, msg: "Selecciona la Fecha de Visita.", color: "warning" });
      return;
    }
    if (!motivoId) {
      setToast({ show: true, msg: "Selecciona el Motivo de Visita.", color: "warning" });
      return;
    }
    if (!comentarios.trim()) {
      setToast({ show: true, msg: "Completa los Comentarios.", color: "warning" });
      return;
    }
    if (!planAccion.trim()) {
      setToast({ show: true, msg: "Completa el Plan de accion.", color: "warning" });
      return;
    }
    if (!fechaPlan) {
      setToast({ show: true, msg: "Selecciona la Fecha plan de accion.", color: "warning" });
      return;
    }
    if (debeMostrarSelectSupervisor && !supervisorLegajo) {
      setToast({ show: true, msg: "Selecciona el Supervisor.", color: "warning" });
      return;
    }
    const faltantes = preguntasOrdenadas.filter((p) => !respuestas[p.id]);
    if (faltantes.length > 0) {
      setToast({ show: true, msg: "Completa todas las respuestas.", color: "warning" });
      return;
    }

    setGuardando(true);
    try {
      const payload = {
        ps_id: psSeleccionado.id,
        fecha_visita: fechaVisita,
        motivo_id: motivoId,
        respuestas: preguntasOrdenadas.map((p) => ({
          pregunta_id: p.id,
          respuesta_codigo: respuestas[p.id],
        })),
        tiene_supervisor: estadoSupervisor === "SI" ? true : estadoSupervisor === "NO" ? false : null,
        supervisor_legajo: estadoSupervisor === "SI" ? supervisorLegajo : undefined,
        supervisor_nombre: estadoSupervisor === "SI" ? nombreSupervisor(supervisorSeleccionado || { nombre: "", apellido: "", nombre_completo: "" }) : undefined,
        comentarios,
        plan_accion: planAccion,
        fecha_plan_accion: fechaPlan,
      };
      await crearEvaluacionVisitas(payload);
      setToast({ show: true, msg: "Evaluacion guardada.", color: "success" });
      setComentarios("");
      setPlanAccion("");
      setFechaPlan(undefined);
      setMotivoId(undefined);
      setRespuestas({});
      limpiarSeleccionSupervisor();
      await cargarEvaluaciones(psSeleccionado.id);
    } catch (error: any) {
      setToast({ show: true, msg: error?.message || "No se pudo guardar la evaluacion.", color: "danger" });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <IonPage>
      <Header titulo="Visitas" />
      <IonContent id="evaluacion-visitas" className="ion-padding">
        {cargando && (
          <div className="ion-padding">
            <IonSpinner />
          </div>
        )}

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Datos de la visita</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonList>
              <IonItem lines="full">
                <IonLabel className="ion-text-wrap">
                  <strong>Punto de Servicio</strong>
                  {psSeleccionado ? (
                    <>
                      <div>{psSeleccionado.ps_nombre || "Sin nombre"}</div>
                      <div className="ps-objeto">Codigo: {psSeleccionado.ps_codigo || psSeleccionado.objeto}</div>
                    </>
                  ) : (
                    <div>Busca y selecciona un punto de servicio.</div>
                  )}
                </IonLabel>
                {psSeleccionado && (
                  <IonButton slot="end" fill="clear" onClick={limpiarSeleccionPs}>
                    Limpiar
                  </IonButton>
                )}
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Buscar Punto de Servicio</IonLabel>
                <IonInput
                  clearInput
                  value={psBusqueda}
                  placeholder="Ingresa al menos 3 letras"
                  onIonChange={(e) => setPsBusqueda((e.detail.value ?? "").toString())}
                />
              </IonItem>

              <IonItem lines="none">
                <IonLabel className="ion-text-wrap">
                  {psErrorBusqueda ? (
                    <IonText color="danger">{psErrorBusqueda}</IonText>
                  ) : psBusqueda.trim().length === 0 ? (
                    <IonText color="medium">Ingresa al menos 3 letras para buscar.</IonText>
                  ) : psBusqueda.trim().length < 3 ? (
                    <IonText color="medium">Escribe {3 - psBusqueda.trim().length} letra(s) mas.</IonText>
                  ) : psResultados.length === 0 && !psBuscando ? (
                    <IonText color="warning">Sin resultados para "{psBusqueda}".</IonText>
                  ) : (
                    <IonText color="medium">Selecciona un resultado.</IonText>
                  )}
                </IonLabel>
                {psBuscando && <IonSpinner slot="end" />}
              </IonItem>

              {psResultados.length > 0 &&
                psResultados.map((ps) => (
                  <IonItem
                    key={ps.id}
                    button
                    detail={false}
                    onClick={() => seleccionarPuntoServicio(ps)}
                    color={psSeleccionado?.id === ps.id ? "light" : undefined}
                  >
                    <IonLabel>
                      <strong>{ps.ps_nombre || "Sin nombre"}</strong>
                      <div>{ps.ps_codigo || ps.objeto}</div>
                    </IonLabel>
                  </IonItem>
                ))}

              <IonItem>
                <IonLabel position="stacked">Fecha de Visita</IonLabel>
                <IonDatetime
                  displayFormat="DD/MM/YYYY"
                  pickerFormat="DD/MM/YYYY"
                  cancelText="Cancelar"
                  doneText="Listo"
                  value={fechaVisita}
                  onIonChange={(e) => setFechaVisita(normalizarFecha(e.detail.value))}
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Fiscal de Operaciones</IonLabel>
                <IonInput value={contexto?.fiscal?.nombre || ""} readonly />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Jefe de Operaciones</IonLabel>
                <IonInput value={contexto?.jefe_operaciones || ""} readonly />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">Motivo de Visita</IonLabel>
                <IonSelect
                  interface="alert"
                  placeholder="Selecciona un motivo"
                  value={motivoId}
                  onIonChange={(e) => setMotivoId(Number(e.detail.value))}
                >
                  {motivos.map((m) => (
                    <IonSelectOption key={m.id} value={m.id}>
                      {m.nombre}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            </IonList>
          </IonCardContent>
        </IonCard>

        {preguntasIniciales.length > 0 && (
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Preguntas iniciales</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {preguntasIniciales.map(renderPregunta)}
            </IonCardContent>
          </IonCard>
        )}

        <IonCard style={{ display: "none" }}>
          <IonCardHeader>
            <IonCardTitle>Supervisor asignado</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonItem>
              <IonLabel position="stacked">¿Tiene supervisor asignado?</IonLabel>
              <IonSelect
                interface="alert"
                value={tieneSupervisor}
                onIonChange={(e) => {
                  const val = (e.detail.value as "SI" | "NO") || "SI";
                  setTieneSupervisor(val);
                  if (val === "NO") {
                    setSupervisorLegajo(undefined);
                    setSupervisorSeleccionado(null);
                    setSupervisorBusqueda("");
                    setSupervisorResultados([]);
                    setSupervisorErrorBusqueda(null);
                  }
                }}
              >
                <IonSelectOption value="SI">Si</IonSelectOption>
                <IonSelectOption value="NO">No</IonSelectOption>
              </IonSelect>
            </IonItem>

            {tieneSupervisor === "SI" && (
              <>
            <IonItem lines="full">
              <IonLabel position="stacked">Buscar supervisor</IonLabel>
              <IonInput
                clearInput
                value={supervisorBusqueda}
                placeholder="Buscar por legajo, nombre o apellido"
                onIonChange={(e) => {
                  const value = String(e.detail.value || "");
                  const etiquetaSeleccionada = supervisorSeleccionado
                    ? `${nombreSupervisor(supervisorSeleccionado)} - ${supervisorSeleccionado.legajo}`
                    : "";
                  setSupervisorBusqueda(value);
                  if (supervisorSeleccionado && value !== etiquetaSeleccionada) {
                    setSupervisorLegajo(undefined);
                    setSupervisorSeleccionado(null);
                  }
                }}
                disabled={!psSeleccionado?.id}
              />
            </IonItem>

            <IonItem lines="none">
              <IonLabel className="ion-text-wrap">
                {supervisorSeleccionado ? (
                  <IonText color="medium">Supervisor seleccionado.</IonText>
                ) : !psSeleccionado?.id ? (
                  <IonText color="medium">Selecciona un Punto de Servicio antes de buscar supervisor.</IonText>
                ) : supervisorErrorBusqueda ? (
                  <IonText color="danger">{supervisorErrorBusqueda}</IonText>
                ) : supervisorBusqueda.trim().length === 0 ? (
                  <IonText color="medium">Ingresa al menos 3 letras para buscar.</IonText>
                ) : supervisorBusqueda.trim().length < 3 ? (
                  <IonText color="medium">Escribe {3 - supervisorBusqueda.trim().length} letra(s) mas.</IonText>
                ) : supervisorResultados.length === 0 && !supervisorBuscando ? (
                  <IonText color="warning">Sin resultados para "{supervisorBusqueda}".</IonText>
                ) : (
                  <IonText color="medium">Selecciona un supervisor de la lista.</IonText>
                )}
              </IonLabel>
              {supervisorBuscando && <IonSpinner slot="end" />}
            </IonItem>

            {supervisorResultados.length > 0 &&
              supervisorResultados.map((s) => (
                <IonItem
                  key={s.legajo}
                  button
                  detail={false}
                  onClick={() => seleccionarSupervisor(s)}
                  color={supervisorLegajo === s.legajo ? "light" : undefined}
                >
                  <IonLabel>
                    <strong>{nombreSupervisor(s)}</strong>
                    <div>{s.legajo} - {s.cargo || "SUPERVISOR"}</div>
                  </IonLabel>
                </IonItem>
              ))}

            {supervisorSeleccionado && (
              <IonItem lines="none">
                <IonLabel className="ion-text-wrap">
                  <strong>Supervisor seleccionado:</strong>{" "}
                  {nombreSupervisor(supervisorSeleccionado)} - {supervisorSeleccionado.legajo}
                </IonLabel>
              </IonItem>
            )}
              </>
            )}
          </IonCardContent>
        </IonCard>

        {preguntasSupervisor.length > 0 && (
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Seccion Supervisor</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {preguntasSupervisor.map(renderPregunta)}
            </IonCardContent>
          </IonCard>
        )}

        {preguntasPersonal.length > 0 && (
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Seccion Personal</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {preguntasPersonal.map(renderPregunta)}
            </IonCardContent>
          </IonCard>
        )}

        {preguntasFinales.length > 0 && (
          <IonCard>
            <IonCardHeader>
              <IonCardTitle>Otras preguntas</IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              {preguntasFinales.map(renderPregunta)}
            </IonCardContent>
          </IonCard>
        )}

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Comentarios y plan de accion</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            <IonList>
              <IonItem>
                <IonLabel position="stacked">Comentarios</IonLabel>
                <IonTextarea
                  autoGrow
                  value={comentarios}
                  onIonChange={(e) => setComentarios(String(e.detail.value || ""))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Plan de accion</IonLabel>
                <IonTextarea
                  autoGrow
                  value={planAccion}
                  onIonChange={(e) => setPlanAccion(String(e.detail.value || ""))}
                />
              </IonItem>
              <IonItem>
                <IonLabel position="stacked">Fecha plan de accion</IonLabel>
                <IonDatetime
                  displayFormat="DD/MM/YYYY"
                  pickerFormat="DD/MM/YYYY"
                  cancelText="Cancelar"
                  doneText="Listo"
                  value={fechaPlan}
                  onIonChange={(e) => setFechaPlan(normalizarFecha(e.detail.value))}
                />
              </IonItem>
            </IonList>
          </IonCardContent>
        </IonCard>

        <div className="ion-padding">
          <IonButton expand="block" onClick={onGuardar} disabled={guardando}>
            {guardando ? "Guardando..." : "Guardar"}
          </IonButton>
        </div>

        <IonCard>
          <IonCardHeader>
            <IonCardTitle>Ultimas visitas</IonCardTitle>
          </IonCardHeader>
          <IonCardContent>
            {listando ? (
              <IonSpinner />
            ) : evaluaciones.length === 0 ? (
              <IonText color="medium">Sin registros.</IonText>
            ) : (
              <IonList>
                {evaluaciones.map((ev) => (
                  <IonItem key={ev.id} lines="full">
                    <IonLabel className="ion-text-wrap">
                      <div>
                        <strong>{ev.ps_nombre || ev.ps_codigo}</strong>
                      </div>
                      <div>{ev.fecha_visita}</div>
                      <div>{ev.motivo_nombre || ""}</div>
                      <div>Calificación: {ev.respuesta_p12 || "Sin respuesta"}</div>
                    </IonLabel>
                    <IonBadge color="medium">{ev.id}</IonBadge>
                  </IonItem>
                ))}
              </IonList>
            )}
          </IonCardContent>
        </IonCard>

        <IonToast
          isOpen={toast.show}
          message={toast.msg}
          color={toast.color}
          duration={2500}
          onDidDismiss={() => setToast({ show: false, msg: "" })}
        />
      </IonContent>
    </IonPage>
  );
};

export default EvaluacionVisitas;
